<template>
    <div class="select-component" :class="{ 'has-error': hasError }" ref="dropdownRef">
        <div
            :id="triggerId"
            class="select-dropdown-trigger"
            @click="toggleDropdown"
            @keydown.enter.prevent="handleTriggerEnter"
            @keydown.space.prevent="toggleDropdown"
            @keydown.down.prevent="handleTriggerDown"
            @keydown.up.prevent="handleTriggerUp"
            @keydown.esc.prevent="closeDropdown"
            tabindex="0"
            role="combobox"
            aria-haspopup="listbox"
            :aria-expanded="isOpen"
            :aria-controls="dropdownId"
            :aria-activedescendant="activeDescendant"
            :aria-label="ariaLabel"
        >
            <div class="select-selected-item" v-if="hasSelectedItem">
                <slot name="selected-item" :selected-item="selectedItem" />
            </div>
            <span v-else class="select-placeholder">{{ placeholder }}</span>
            <span class="select-dropdown-arrow icon is-small" aria-hidden="true"><i class="fas fa-angle-down"></i></span>
        </div>

        <div
            :id="dropdownId"
            :class="['select-dropdown-menu', dropdownMode === 'container' ? 'select-dropdown-menu--container' : '']"
            v-if="isOpen"
            role="listbox"
            :aria-labelledby="triggerId"
        >
            <button
                class="select-dropdown-close is-hidden-tablet"
                @click="closeDropdown"
                tabindex="0"
                :aria-label="translate('common.close')"
            >
                <i class="fas fa-times" aria-hidden="true"></i>
            </button>

            <div class="select-dropdown-content">
                <input
                    v-if="searchable"
                    ref="searchInput"
                    class="input select-search-input"
                    :value="searchQuery"
                    @input="e => (searchQuery = (e.target as HTMLInputElement)?.value)"
                    type="text"
                    autocomplete="off"
                    :placeholder="searchPlaceholder"
                    :aria-label="searchPlaceholder || translate('common.search')"
                    @keydown.stop
                    @keydown.down.prevent="focusNextOption"
                    @keydown.up.prevent="focusPrevOption"
                    @keydown.home.prevent="focusFirstOption"
                    @keydown.end.prevent="focusLastOption"
                    @keydown.esc.prevent="closeDropdown"
                    @keydown.enter.prevent="selectFocusedOption"
                />

                <!-- Screen reader only live region for search results -->
                <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
                    {{ searchResultsAnnouncement }}
                </div>

                <template v-if="allowCancelChoice">
                    <div
                        :id="`${dropdownId}-cancel`"
                        class="select-dropdown-item select-dropdown-item--cancel-choice"
                        :class="{ 'select-is-focused': focusedIndex === -1 }"
                        @click="selectItem({ [itemKey]: null } as T)"
                        @mouseenter="onItemMouseEnter(-1)"
                        @mousemove="isUsingKeyboard = false"
                        role="option"
                        :aria-selected="model === null"
                    >
                        <slot name="cancel-choice">
                            <span class="icon" aria-hidden="true">
                                <i class="fas fa-times-circle"></i>
                            </span>
                            <span>Clear selection</span>
                        </slot>
                    </div>
                </template>

                <div
                    v-for="(item, index) in filteredItems"
                    :key="getItemKey(item, index)"
                    :id="`${dropdownId}-option-${index}`"
                    class="select-dropdown-item"
                    :class="{ 'select-is-focused': focusedIndex === index }"
                    @click="selectItem(item)"
                    @mouseenter="onItemMouseEnter(index)"
                    @mousemove="isUsingKeyboard = false"
                    role="option"
                    :aria-selected="isItemSelected(item)"
                >
                    <slot name="item" :item="item" />
                </div>
                <div v-if="filteredItems.length === 0" class="select-dropdown-item select-is-disabled" role="alert">
                    <slot name="no-results">
                        <span class="icon" aria-hidden="true">
                            <i class="fas fa-exclamation-triangle"></i>
                        </span>
                        <span>{{ noResultsText }}</span>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts" generic="T extends NonNullable<unknown>, K = T[keyof T]">
    import { ref, computed, onMounted, onBeforeUnmount, watch, defineModel, nextTick } from "vue";
    import objectMatcher from "./objectMatcher";
    import { translate } from "./translations";

    const props = defineProps<{
        items: T[];
        itemKey: string;
        itemText?: string;
        searchable?: boolean;
        searchKeys?: string[];
        placeholder?: string;
        searchPlaceholder?: string;
        noResultsText?: string;
        dropdownMode?: "absolute" | "container";
        hasError?: boolean;
        allowCancelChoice?: boolean;
        ariaLabel?: string;
    }>();

    const model = defineModel<K | null>();

    const emit = defineEmits(["open", "close"]);

    const isOpen = ref(false);
    const searchQuery = ref("");
    const dropdownRef = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const focusedIndex = ref<number>(-1);
    const isUsingKeyboard = ref(false);

    // Generate unique IDs for ARIA relationships
    const uniqueId = Math.random().toString(36).substring(2, 11);
    const dropdownId = `select-dropdown-${uniqueId}`;
    const triggerId = `select-trigger-${uniqueId}`;

    const filteredItems = computed(() => {
        if (!searchQuery.value || !props.searchable) {
            return props.items;
        }

        const searchTerm = searchQuery.value.toLowerCase();

        return props.items.filter(item => {
            return objectMatcher.matchString(searchTerm, getSearchableText(item).toLowerCase());
        });
    });

    function getSearchableText(item: T): string {
        if (!props.searchKeys) {
            return JSON.stringify(item);
        }
        return props.searchKeys.map(key => item[key]).join(" ");
    }

    const selectedItem = computed(() => {
        if (model.value === null || model.value === undefined) {
            return null;
        }

        return (
            props.items.find(item => {
                return item[props.itemKey] === model.value;
            }) || null
        );
    });

    const hasSelectedItem = computed(() => {
        return selectedItem.value !== null;
    });

    const activeDescendant = computed(() => {
        if (!isOpen.value || focusedIndex.value < 0) {
            return undefined;
        }
        if (props.allowCancelChoice && focusedIndex.value === -1) {
            return `${dropdownId}-cancel`;
        }
        return `${dropdownId}-option-${focusedIndex.value}`;
    });

    const searchResultsAnnouncement = computed(() => {
        if (!props.searchable || !searchQuery.value) {
            return "";
        }
        const count = filteredItems.value.length;
        if (count === 0) {
            return translate("common.no_results_found");
        }
        return `${count} ${count === 1 ? "result" : "results"} found`;
    });

    function close() {
        isOpen.value = false;
    }

    defineExpose({ close });

    function getItemKey(item: T, index: number): string | number {
        if (typeof item === "object" && item !== null && props.itemKey && item[props.itemKey] !== undefined) {
            return item[props.itemKey];
        }
        return index;
    }

    function isItemSelected(item: T): boolean {
        return item[props.itemKey] === model.value;
    }

    function onItemMouseEnter(index: number) {
        if (!isUsingKeyboard.value) {
            focusedIndex.value = index;
        }
    }

    function selectItem(item: T) {
        model.value = item[props.itemKey];
        isOpen.value = false;
        emit("close");
        searchQuery.value = "";
        focusedIndex.value = -1;
        // Return focus to the trigger element after selection
        nextTick(() => {
            returnFocusToTrigger();
        });
    }

    function selectFocusedOption() {
        if (focusedIndex.value === -1 && props.allowCancelChoice) {
            selectItem({ [props.itemKey]: null } as T);
        } else if (focusedIndex.value >= 0 && focusedIndex.value < filteredItems.value.length) {
            selectItem(filteredItems.value[focusedIndex.value]);
        }
    }

    function toggleDropdown() {
        isOpen.value = !isOpen.value;
        if (isOpen.value) {
            emit("open");
            focusedIndex.value = -1;
            if (props.searchable) {
                searchQuery.value = "";
                nextTick(() => {
                    searchInput.value?.focus();
                });
            }
        } else {
            emit("close");
        }
    }

    function closeDropdown() {
        isOpen.value = false;
        emit("close");
        focusedIndex.value = -1;
        // Return focus to trigger
        nextTick(() => {
            returnFocusToTrigger();
        });
    }

    function openAndFocusFirst() {
        isOpen.value = true;
        emit("open");
        focusedIndex.value = 0;
        nextTick(() => {
            if (props.searchable && searchInput.value) {
                searchInput.value.focus();
            }
        });
    }

    function openAndFocusLast() {
        isOpen.value = true;
        emit("open");
        focusedIndex.value = filteredItems.value.length - 1;
        nextTick(() => {
            if (props.searchable && searchInput.value) {
                searchInput.value.focus();
            }
        });
    }

    function handleTriggerDown() {
        if (!isOpen.value) {
            openAndFocusFirst();
        } else {
            focusNextOption();
        }
    }

    function handleTriggerUp() {
        if (!isOpen.value) {
            openAndFocusLast();
        } else {
            focusPrevOption();
        }
    }

    function handleTriggerEnter() {
        if (!isOpen.value) {
            toggleDropdown();
        } else {
            selectFocusedOption();
        }
    }

    function focusNextOption() {
        if (filteredItems.value.length === 0) {
            return;
        }
        isUsingKeyboard.value = true;

        if (focusedIndex.value < filteredItems.value.length - 1) {
            focusedIndex.value++;
        } else {
            focusedIndex.value = 0; // Wrap to first
        }
        scrollFocusedIntoView();
    }

    function focusPrevOption() {
        if (filteredItems.value.length === 0) {
            return;
        }
        isUsingKeyboard.value = true;

        if (focusedIndex.value > 0) {
            focusedIndex.value--;
        } else if (focusedIndex.value === 0) {
            focusedIndex.value = filteredItems.value.length - 1; // Wrap to last
        } else {
            focusedIndex.value = filteredItems.value.length - 1;
        }
        scrollFocusedIntoView();
    }

    function focusFirstOption() {
        if (filteredItems.value.length === 0) {
            return;
        }
        isUsingKeyboard.value = true;
        focusedIndex.value = 0;
        scrollFocusedIntoView();
    }

    function focusLastOption() {
        if (filteredItems.value.length === 0) {
            return;
        }
        isUsingKeyboard.value = true;
        focusedIndex.value = filteredItems.value.length - 1;
        scrollFocusedIntoView();
    }

    function scrollFocusedIntoView() {
        nextTick(() => {
            const focusedElement = document.getElementById(`${dropdownId}-option-${focusedIndex.value}`);
            if (focusedElement) {
                focusedElement.scrollIntoView({ block: "nearest", behavior: "auto" });
            }
        });
    }

    function returnFocusToTrigger() {
        const trigger = document.getElementById(triggerId);
        if (trigger) {
            trigger.focus();
        }
    }

    function handleClickOutside(event: MouseEvent) {
        if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
            if (isOpen.value) {
                isOpen.value = false;
                emit("close");
                focusedIndex.value = -1;
            }
        }
    }

    onMounted(() => {
        document.addEventListener("mousedown", handleClickOutside);
    });

    onBeforeUnmount(() => {
        document.removeEventListener("mousedown", handleClickOutside);
    });

    watch(
        () => model.value,
        () => {
            // Reset search on value change
            searchQuery.value = "";
        },
    );

    watch(searchQuery, () => {
        // Reset focused index when search changes
        focusedIndex.value = 0;
    });
</script>
